/**
 * Search Within Map Plugin for Search Results Map Variant
 *
 * Manages the rectangular-area search flow: showing or hiding the
 * "search within map" button, dispatching a fetch when the user clicks it,
 * re-fetching as the map moves, and re-centering when the user's current
 * location is resolved.
 *
 * Single responsibility: decide when and how a rectangular-area search should
 * be triggered based on map state and user interaction.
 *
 * This plugin does NOT:
 * - Create or configure the map instance (see map.plugin)
 * - Pan the map to a new typeahead origin (see mapOriginSync.plugin)
 * - Sync markers or boundaries (see markers.plugin, mapBoundaries.plugin)
 *
 * Reason to change: how the rectangular-area search is triggered, debounced,
 * or initiated from geolocation.
 *
 * @module searchResultsMap/plugins/searchWithinMap.plugin
 */

import {
  fromEvent,
  distinctUntilChanged,
  debounceTime,
  filter,
  map,
  pairwise,
  skip
} from 'rxjs';
import {
  LocationType,
  searchParametersActions,
  selectLocationType
} from 'features/searchResultsFacets';
import { selectGeoCoordinates } from '../redux/selectors';
import { getViewportPaddingForSitePlans, expandBounds } from '../services/sitePlanViewportPadding';
import { currentLocationProvided$ } from '../redux/observables';
import { RECTANGULAR_AREA_MIN_ZOOM, DEFAULT_MIN_ZOOM } from '../constants';

const RECTANGULAR_AREA = LocationType.RectangularArea.toString();
const STATE = LocationType.State.toString();

const areDifferentCoordinates = (previous, current) =>
  previous.east !== current.east ||
  previous.north !== current.north ||
  previous.south !== current.south ||
  previous.west !== current.west;

const createFetchLocationsPayload = (bounds, zoom) => {
  if (!bounds) {
    return null;
  }

  // Expand bounds slightly when site plans are active so partially-visible site plan
  // polygons near the viewport edges are not prematurely dropped from results.
  const padding = getViewportPaddingForSitePlans(zoom ?? 0);
  const paddedBounds = padding > 0 ? expandBounds(bounds, padding) : bounds;

  const { south, north, west, east } = paddedBounds;
  if (![south, north, west, east].every(Number.isFinite)) {
    return null;
  }

  return {
    locationType: RECTANGULAR_AREA,
    minimumLatitude: south,
    maximumLatitude: north,
    minimumLongitude: west,
    maximumLongitude: east,
  };
};

export default function createSearchWithinMapPlugin({ triggerButton = null } = {}) {
  const state = {
    started: false,
    subscriptions: [],
    mapFacade: null,
    store$: null,
    dispatch: null,
    getState: null,
    triggerElement: null,
  };

  function dispatchFetchLocations(bounds) {
    const zoom = state.mapFacade?.getCameraSnapshot?.()?.zoom ?? 0;
    const payload = createFetchLocationsPayload(bounds, zoom);
    if (!payload) {
      return;
    }

    state.dispatch(searchParametersActions.fetchLocations(payload));
  }

  function subscribeToLocationTypeChange() {
    state.subscriptions.push(
      state.store$.pipe(
        map(selectLocationType),
        distinctUntilChanged(),
        pairwise(),
        filter(([prev, curr]) => prev === RECTANGULAR_AREA || curr === RECTANGULAR_AREA),
      ).subscribe(([, curr]) => {
        const minZoom = curr === RECTANGULAR_AREA ? RECTANGULAR_AREA_MIN_ZOOM : DEFAULT_MIN_ZOOM;
        state.mapFacade?.onceMapEvent?.('idle', () => {
          state.mapFacade?.setMapMinZoom?.(minZoom);
        });
      }),
    );
  }

  function subscribeToButtonVisibility() {
    state.subscriptions.push(
      state.store$.pipe(
        map(selectLocationType),
        distinctUntilChanged(),
      ).subscribe((locationType) => {
        state.triggerElement.hidden = locationType === RECTANGULAR_AREA || locationType === STATE;
      }),
    );
  }

  function subscribeToMapMoves() {
    state.subscriptions.push(
      state.store$.pipe(
        filter((currentState) => selectLocationType(currentState) === RECTANGULAR_AREA),
        map(selectGeoCoordinates),
        pairwise(),
        filter(([previousCoordinates, currentCoordinates]) => previousCoordinates && currentCoordinates),
        filter(([previousCoordinates, currentCoordinates]) =>
          areDifferentCoordinates(previousCoordinates, currentCoordinates)),
        map(([, currentCoordinates]) => currentCoordinates),
        // debounce to avoid excessive dispatches during continuous map movements,
        // while ensuring the last position is dispatched after movement ends
        debounceTime(300),
        skip(1), // skip initial emission on subscription
      ).subscribe((currentCoordinates) => {
        dispatchFetchLocations(currentCoordinates);
      }),
    );
  }

  function centerMapOnLocation(location) {
    state.mapFacade?.updateMapCenter?.({
      latitude: location.latitude,
      longitude: location.longitude,
    });
  }

  function fetchLocationsOnIdle() {
    // updateMapCenter and setMapMinZoom are async — the map animates to the
    // new position before the idle event fires. Read bounds only once the map
    // has settled so we dispatch the correct post-pan coordinates.
    state.mapFacade?.onceMapEvent?.('idle', () => {
      dispatchFetchLocations(state.mapFacade?.getCameraSnapshot?.()?.bounds);
    });
  }

  function handleCurrentLocationArrival(currentLocation) {
    centerMapOnLocation(currentLocation);
    fetchLocationsOnIdle();
  }

  function hasCoordinates({ latitude, longitude }) {
    return latitude != null && longitude != null;
  }

  function subscribeToCurrentLocationArrival() {
    state.subscriptions.push(
      currentLocationProvided$.pipe(
        map(({ latitude, longitude }) => ({ latitude, longitude })),
        filter(hasCoordinates),
      ).subscribe(handleCurrentLocationArrival),
    );
  }

  function subscribeToButtonClick() {
    state.subscriptions.push(
      fromEvent(state.triggerElement, 'click').subscribe(() => {
        dispatchFetchLocations(state.mapFacade?.getCameraSnapshot?.()?.bounds);
      }),
    );
  }

  function clearSubscriptions() {
    state.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
    state.subscriptions = [];
  }

  return {
    name: 'search-within-map',

    async init(context) {
      context.state.searchWithinMap = {
        started: false,
      };
    },

    async start(context) {
      if (state.started) {
        return;
      }

      state.mapFacade = context.services.mapFacade;
      state.store$ = context.services.store$;
      state.dispatch = context.services.dispatch;
      state.getState = context.services.getState;
      state.triggerElement = triggerButton;

      if (!state.mapFacade || !state.store$ || !state.dispatch || !state.getState) {
        return;
      }

      if (state.triggerElement) {
        subscribeToButtonClick();
        subscribeToButtonVisibility();
        subscribeToMapMoves();
      }

      subscribeToCurrentLocationArrival();
      subscribeToLocationTypeChange();

      state.started = true;
      context.state.searchWithinMap = {
        started: true,
      };
    },

    async stop(context) {
      clearSubscriptions();

      state.started = false;
      state.mapFacade = null;
      state.store$ = null;
      state.dispatch = null;
      state.getState = null;
      state.triggerElement = null;

      delete context.state.searchWithinMap;
    },
  };
}
